import { reviewSignLine, reviewSummaryOf, todayHoursOf, type PublicShop } from "@fusiqo/vozqi-shared";
import { useState, type RefObject } from "react";
import { ProfileActions } from "./profile-actions";
import { useNow } from "./use-now";

/**
 * «QUIÉN ES» (spec 2026-10-20 §5.1-2): the business's sign, a professional
 * page. Without a cover photo, an
 * awning drawn from the house's own blue -- it needs nothing uploaded, so no
 * profile opens bare -- with the logo hanging from it; the name as the
 * page's title, the trade in its zone, its reviews' average from three,
 * whether it is open right now, and the
 * one thing to do, «Pedir servicio» for a trade that takes requests (the
 * floating button is WhatsApp's) -- on a wide screen with a rail, it heads
 * the rail instead (ShopPage, profile.css). The street is «Dónde estamos»'s,
 * further down.
 *
 * «FOTO DE PORTADA», IMMERSIVE: the business's own photo fills the whole
 * hero, and the identity and the action sit over its
 * lower part on a dark scrim -- profile.css says how readable is guaranteed.
 * Without one, or if it fails to load, the awning: a profile never opens on
 * a hole. Both draw the same tree after the photo, so the name the header
 * watches stays the same element when a photo fails.
 */
export function ProfileHero({
  shop,
  logoSrc,
  nameRef,
}: {
  shop: PublicShop;
  logoSrc: string | null;
  /** The name, watched by the header: it shows the business once this scrolls away. */
  nameRef: RefObject<HTMLHeadingElement | null>;
}) {
  const { profile, business } = shop;
  // The address that failed, not a flag: a new photo («Cambiar») is a new
  // address and gets its own try instead of staying on the awning.
  const [failedCover, setFailedCover] = useState<string | null>(null);
  const cover = profile.cover === failedCover ? null : profile.cover;
  const rating = reviewSignLine(reviewSummaryOf(profile.reviews.count, profile.reviews.sumStars));
  return (
    <section className={cover === null ? "profile-hero" : "profile-hero profile-hero--cover"} aria-labelledby="profile-name">
      {cover === null ? (
        <div className="profile-awning" aria-hidden="true" />
      ) : (
        // Decorative: the name over it says whose it is. Laid over the hero's
        // box rather than sizing it, so nothing jumps as it loads; the page's
        // largest paint, so it is fetched first.
        <img
          className="profile-cover"
          src={cover}
          alt=""
          loading="eager"
          fetchPriority="high"
          onError={() => setFailedCover(profile.cover)}
        />
      )}
      <div className="profile-hero-inner">
        <div className="profile-id">
          {logoSrc === null ? (
            <span className="profile-logo profile-logo--initial" aria-hidden="true">
              {business.name.trim().charAt(0).toUpperCase()}
            </span>
          ) : (
            <img className="profile-logo" src={logoSrc} alt="" width={88} height={88} />
          )}
          <div className="profile-id-text">
            <h1 id="profile-name" ref={nameRef} className="profile-name">
              {business.name}
            </h1>
            <TradeLine trade={profile.trade} zone={profile.zone} />
            {rating === null ? null : <p className="profile-trade">{rating}</p>}
            <TodayLine profile={profile} />
            <p className="profile-since">En VozQI desde {profile.since}</p>
          </div>
        </div>
        <ProfileActions token={profile.requestToken} requestLabel="Pedir servicio" />
      </div>
    </section>
  );
}

/** «Taller o servicio técnico en Bella Vista, Panamá»: the trade, the zone, or both in one phrase. */
export function TradeLine({ trade, zone }: { trade: string | null; zone: string | null }) {
  if (trade === null && zone === null) return null;
  return (
    <p className="profile-trade">
      {trade ?? ""}
      {trade !== null && zone !== null ? " en " : ""}
      {zone === null ? null : <span className="profile-zone">{zone}</span>}
    </p>
  );
}

/** «Abierto ahora. Cierra a las 5:00 p.m.», on the business's clock and kept current; nothing without hours. */
export function TodayLine({ profile }: { profile: PublicShop["profile"] }) {
  const today = todayHoursOf(profile.hours, profile.timezone, useNow());
  if (today === null) return null;
  return (
    <p className={today.open ? "profile-today profile-today--open" : "profile-today"}>
      <span className="profile-today-state">{today.state}.</span> {today.detail}
    </p>
  );
}
